import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Stack, useMantineTheme } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IColumn } from '@feathr/components';
import {
  Button,
  CardV2,
  PageHeader,
  Pill,
  Table,
  TableColumnHeader,
  Text,
  Toolbar,
} from '@feathr/components';

import PrototypeDoneModal from '../components/PrototypeDoneModal';
import ReportNameCell from '../components/ReportNameCell';
import type { ISampleReport } from '../sampleData';
import { sampleReports } from '../sampleData';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';
import * as shellStyles from '../Shell/PrototypeShell.css';

const SCOPE_LABEL: Record<ISampleReport['scope'], string> = {
  account: 'Account',
  project: 'Project',
  flight: 'Flight',
};

function buildReportColumns(
  t: TFunction,
  onSelect: (report: ISampleReport) => void,
): Array<IColumn<ISampleReport>> {
  return [
    {
      accessor: 'name',
      id: 'name',
      Header: TableColumnHeader({ title: t('Report'), sortType: 'alpha' }),
      headerClassName: tableStyles.cellLeft,
      sortable: true,
      className: tableStyles.cellLeft,
      Cell({ original }): JSX.Element {
        return <ReportNameCell onSelect={onSelect} report={original} />;
      },
    },
    {
      accessor: 'scope',
      id: 'scope',
      Header: TableColumnHeader({ title: t('Scope'), sortType: 'alpha' }),
      width: 140,
      headerClassName: tableStyles.cellLeft,
      sortable: true,
      className: tableStyles.cellLeft,
      Cell({ original }): JSX.Element {
        return <Pill theme={'neutral'}>{t(SCOPE_LABEL[original.scope])}</Pill>;
      },
    },
    {
      accessor: 'value',
      id: 'value',
      Header: TableColumnHeader({ title: t('Value'), sortType: 'alpha' }),
      width: 160,
      headerClassName: tableStyles.cellRight,
      sortable: true,
      className: tableStyles.cellRight,
      Cell({ original }): JSX.Element {
        return <Text variant={'defaultMedium'}>{original.value}</Text>;
      },
    },
    {
      accessor: 'delta',
      id: 'delta',
      Header: TableColumnHeader({ title: t('Change'), sortType: 'alpha' }),
      width: 120,
      headerClassName: tableStyles.cellRight,
      sortable: true,
      className: tableStyles.cellRight,
      Cell({ original }): JSX.Element {
        const isPositive = !original.delta.startsWith('-');
        return <Pill theme={isPositive ? 'green' : 'red'}>{original.delta}</Pill>;
      },
    },
  ];
}

function ReportingPage(): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [isDoneOpen, setDoneOpen] = useState<boolean>(false);
  const [doneTitle, setDoneTitle] = useState<string>('');

  function handleSelectReport(report: ISampleReport): void {
    setDoneTitle(t('Report — {{name}}', { name: report.name }));
    setDoneOpen(true);
  }

  const reportColumns = useMemo(() => buildReportColumns(t, handleSelectReport), [t]);

  const actions = (
    <Toolbar>
      <Button prefix={<FontAwesomeIcon icon={faDownload} />} type={'secondary'}>
        {t('Export as CSV')}
      </Button>
    </Toolbar>
  );

  return (
    <>
      <PageHeader actions={actions} title={t('Reporting')} />
      <Stack className={shellStyles.body}>
        <CardV2 width={'full'}>
          <CardV2.Content title={t('Audience engagement')}>
            <Stack gap={theme.spacing['2']}>
              <Text variant={'smallRegular'}>
                {t('Engagement trends across your audience over time.')}
              </Text>
            </Stack>
          </CardV2.Content>
        </CardV2>
        <CardV2 width={'full'}>
          <CardV2.Content title={t('Email performance')}>
            <Stack gap={theme.spacing['2']}>
              <Text variant={'smallRegular'}>
                {t('Open rates, click rates, and conversions across email campaigns.')}
              </Text>
            </Stack>
          </CardV2.Content>
        </CardV2>
        <CardV2 width={'full'}>
          <CardV2.Content title={t('Ad performance')}>
            <Stack gap={theme.spacing['2']}>
              <Text variant={'smallRegular'}>
                {t('Reach, clicks, and conversions across paid campaigns.')}
              </Text>
            </Stack>
          </CardV2.Content>
        </CardV2>
        <Table<ISampleReport>
          columns={reportColumns}
          idKey={'id'}
          isPaginated={false}
          items={sampleReports}
        />
      </Stack>

      <PrototypeDoneModal
        description={t('The full report opens at this point. The prototype ends here.')}
        onClose={(): void => setDoneOpen(false)}
        opened={isDoneOpen}
        title={doneTitle}
      />
    </>
  );
}

export default ReportingPage;
